import ContentLoader from 'react-content-loader'
import { useMemo } from 'react'
import styled, { useTheme } from 'styled-components'
import Decimal from 'decimal.js'

import { PhantomTypeArgument, TypeArgument } from '@/shared/gen/_framework/reified'
import { DevInspectLpUnclaimedRewardsResult, PositionConfigInfo, Amount, CoinInfo, USDC } from '@kunalabs-io/kai'
import { PositionInfo } from '@/shared/kai/hooks/useUserPositions'

import { IconInfo } from 'components/Icons/InfoIcon'
import { ReversedWrapper } from 'components/ReversedWrapper/ReversedWrapper'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { Typography } from 'components/Typography/Typography'
import { useTokenPrice } from 'utils/useTokenPrice'
import { useRewardsFeesTotalValue } from '../../hooks/useRewardsFeesTotalValue'
import { useRewardsFeesInfo } from '../../hooks/useRewardFeeInfos'
import { useIsMobile } from 'utils/useIsMobile'
import { getFormattedUsdValue } from 'utils/usdFormatters'

const SectionLabel = styled(Typography)`
  display: flex;
  align-items: center;
  margin-top: 16px;
  margin-bottom: 16px;
  & > div {
    display: flex;
  }
  svg {
    cursor: pointer;
    margin-left: 6px;
    path {
      stroke: ${p => p.theme.colors.text.secondary};
    }
  }
`

const ErrorLabel = styled(Typography)`
  color: ${p => p.theme.colors.text.danger};
`

const Total = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const Divider = styled.div`
  height: 1px;
  width: 100%;
  background-color: ${p => p.theme.colors.divider};
  margin-top: 22px;
  margin-bottom: 8px;
`

const UsdEstimate = styled(Typography)`
  position: absolute;
  right: 0;
`

const PositionBreakdownContainer = styled.div`
  padding: 20px 16px;
  border-radius: 8px;
  border: 1px solid ${p => p.theme.colors.border};
  background: ${p => p.theme.colors.background.secondary};
`

const PositionBreakdownContainerItem = styled.div`
  display: flex;
  justify-content: space-between;
  position: relative;
`

const PositionBreakdownSymbol = styled(Typography)`
  display: flex;
  svg {
    margin-right: 8px;
    border-radius: 50%;
  }
`

const InfoContainer = styled.div`
  display: block;
`

interface Props {
  isReversedPair: boolean
  rewards: DevInspectLpUnclaimedRewardsResult | null
  configInfo?: PositionConfigInfo<string, string, TypeArgument>
  isWithinWithdraw?: boolean
  positionInfo: PositionInfo
}

interface RewardItemProps {
  coinInfo: CoinInfo<PhantomTypeArgument>
  amount: Amount
  isLast: boolean
}

interface FeeItemProps {
  coinInfo: CoinInfo<PhantomTypeArgument>
  amount: Amount
  marginBottom: string
}

const PendingFeesRewardsTotalContentLoader = () => {
  const theme = useTheme()

  return (
    <ContentLoader
      speed={2}
      width={130}
      height={20}
      viewBox="0 0 130 20"
      backgroundColor={theme.colors.contentLoader.backgroundColor}
      foregroundColor={theme.colors.contentLoader.foregroundColor}
    >
      <rect x="0" y="0" rx="4" ry="4" width="130" height="20" />
    </ContentLoader>
  )
}

const RewardItem = ({ coinInfo, amount, isLast }: RewardItemProps) => {
  const { isLoading, price } = useTokenPrice(coinInfo, USDC)

  const rewardsValueUsd = useMemo(() => {
    if (!price || isLoading) {
      return ''
    }

    return getFormattedUsdValue(new Decimal(0).plus(amount.toDecimal().mul(price.human)).toNumber())
  }, [price, isLoading, amount])

  return (
    <PositionBreakdownContainerItem key={coinInfo.typeName} style={isLast ? {} : { marginBottom: 20 }}>
      <PositionBreakdownSymbol variant="description" fontWeight="semiBold">
        <img src={coinInfo.iconUrl} alt={coinInfo.displaySymbol} width={24} height={24} />
        &nbsp;
        {coinInfo.displaySymbol}
      </PositionBreakdownSymbol>
      <Typography variant="description" fontWeight="semiBold">
        {amount.toDecimal().toString()}
        <UsdEstimate variant="caption" color="secondary">
          {rewardsValueUsd}
        </UsdEstimate>
      </Typography>
    </PositionBreakdownContainerItem>
  )
}

const FeeItem = ({ coinInfo, amount, marginBottom }: FeeItemProps) => {
  const { isLoading, price } = useTokenPrice(coinInfo, USDC)

  const feeValueUsd = useMemo(() => {
    if (!price || isLoading) {
      return ''
    }

    return getFormattedUsdValue(new Decimal(0).plus(amount.toDecimal().mul(price.human)).toNumber())
  }, [price, isLoading, amount])

  return (
    <PositionBreakdownContainerItem style={{ marginBottom: marginBottom }}>
      <PositionBreakdownSymbol variant="description" fontWeight="semiBold">
        <img src={coinInfo.iconUrl} alt={coinInfo.displaySymbol} width={24} height={24} />
        &nbsp;
        {coinInfo.displaySymbol}
      </PositionBreakdownSymbol>
      <div style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
        <Typography variant="description" fontWeight="semiBold">
          {amount.toDecimal().toString()}
          <UsdEstimate variant="caption" color="secondary">
            {feeValueUsd}
          </UsdEstimate>
        </Typography>
      </div>
    </PositionBreakdownContainerItem>
  )
}

interface RewardsProps {
  rewards: DevInspectLpUnclaimedRewardsResult | null
}

const Rewards = ({ rewards }: RewardsProps) => {
  const otherRewardsMap = useMemo(() => {
    const map = new Map<string, { coinInfo: CoinInfo<PhantomTypeArgument>; amount: Amount }>()
    if (!rewards) {
      return map
    }

    const mergeAmount = (coinType: CoinInfo<PhantomTypeArgument>, amount: bigint) => {
      const existingAmount = map.get(coinType.typeName)?.amount.int ?? 0n
      map.set(coinType.typeName, { coinInfo: coinType, amount: coinType.newAmount(existingAmount + amount) })
    }
    for (const [coinType, amount] of rewards.rewards) {
      mergeAmount(coinType, amount.int)
    }
    for (const [coinType, amount] of rewards.stashed) {
      mergeAmount(coinType, amount.int)
    }

    return map
  }, [rewards])

  return Array.from(otherRewardsMap.values()).map((entry, index, array) => (
    <RewardItem
      key={entry.coinInfo.typeName}
      coinInfo={entry.coinInfo}
      amount={entry.amount}
      isLast={index === array.length - 1}
    />
  ))
}

interface FeesRewardsTotal {
  positionInfo: PositionInfo
}

const FeesRewardsTotal = ({ positionInfo }: FeesRewardsTotal) => {
  const {
    result: rewardsFeesInfos,
    isLoading: isLoadingRewardsFees,
    error: rewardsFeesInfoError,
  } = useRewardsFeesInfo(positionInfo)

  const { isLoading: isLoadingTotalValue, totalSum } = useRewardsFeesTotalValue(positionInfo, rewardsFeesInfos, USDC)

  const isLoading = isLoadingRewardsFees || isLoadingTotalValue || rewardsFeesInfos === null

  return (
    <Total>
      <Typography variant="description" fontWeight="semiBold">
        TOTAL
      </Typography>
      {isLoading ? (
        <PendingFeesRewardsTotalContentLoader />
      ) : rewardsFeesInfoError ? (
        <ErrorLabel variant="body">Something went wrong while loading fees and rewards</ErrorLabel>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {typeof totalSum !== 'undefined' ? (
            <>
              <Typography variant="description" fontWeight="semiBold">
                {getFormattedUsdValue(totalSum.toNumber())}
              </Typography>
            </>
          ) : null}
        </div>
      )}
    </Total>
  )
}

export const PendingFeesRewardsNew = ({
  isReversedPair,
  configInfo,
  rewards,
  isWithinWithdraw,
  positionInfo,
}: Props) => {
  const isMobile = useIsMobile()

  if (!configInfo || !rewards) {
    return null
  }

  return (
    <InfoContainer>
      <div>
        <SectionLabel variant="body" color="secondary">
          Pending LP fees & rewards
          <Tooltip
            content="Unclaimed fees & rewards accrued from providing liquidity."
            position={isWithinWithdraw ? 'bottom' : 'top'}
            tooltipBoxStyles={isMobile ? { minWidth: 250 } : { marginLeft: -50, minWidth: 250 }}
          >
            <IconInfo width={16} height={16} />
          </Tooltip>
        </SectionLabel>
        <PositionBreakdownContainer>
          <ReversedWrapper
            childX={<FeeItem coinInfo={configInfo.X} amount={rewards.x} marginBottom={isReversedPair ? '' : '20px'} />}
            childY={<FeeItem coinInfo={configInfo.Y} amount={rewards.y} marginBottom={isReversedPair ? '20px' : ''} />}
            isReversed={isReversedPair}
          />
          <Divider />
          <Rewards rewards={rewards} />
          <Divider />
          <FeesRewardsTotal positionInfo={positionInfo} />
        </PositionBreakdownContainer>
      </div>
    </InfoContainer>
  )
}
